import styles from './edit-version-dialog.module.scss';
import { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { v4 } from 'uuid';
import {
  Accordion, AccordionDetails, AccordionSummary, CheckboxLabel, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, Divider, GrayOutlinedButton, Icon, MenuItem, PrimaryButton,
  SecondaryButton, Select, SnackBarContext, TextField, Typography
} from '@frontend-workspace/react-shared-lib';
import { ProductFileEnum } from '../../_enums/product-file';
import { IProductVersion } from '../../_models/product-version';
import { IProductFile } from '../../_models/product-file';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../../requestUtils';

/* eslint-disable-next-line */
export interface EditVersionDialogProps {
  open: boolean,
  onClose: () => void,
  getData: () => void,
  version: IProductVersion,
  versionGroupId: string,
  relationProductId: string
  lastReleaseId: string | undefined
}

export function EditVersionDialog(props: EditVersionDialogProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState(props.version.files);

  useEffect(() => {
    setFiles(props.version.files);
  }, [props.version]);

  const handleNewFile = () => {
    const newFile: IProductFile = {
      id: v4(),
      description: t("editVersion.newFile"),
      downloadUrl: "",
      mainModuleVersion: "",
      releaseDate: new Date(),
      versionType: ProductFileEnum.MainProduct
    }
    const newFiles = [newFile].concat(files);
    setFiles(newFiles);
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("standard.changeVersion")}
      </DialogTitle>
      <DialogContent>
        <div className='mb-3'>
          <PrimaryButton
            variant="outlined"
            onClick={handleNewFile}
            className='minButtonWidth'>
            <Icon className="me-1" fontSize="small">add</Icon>{t("editVersion.newFile")}
          </PrimaryButton>
        </div>
        {files.map((f, index) =>
          <Accordion key={index}>
            <AccordionSummary
              expandIcon={<Icon>expand_more</Icon>}>
              <Typography>
                {f.description}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <File
                key={f.id}
                file={f}
                getData={props.getData}
                isLastFile={props.lastReleaseId === f.id}
                relationProductId={props.relationProductId}
                versionId={props.version.id}
                versionGroupId={props.versionGroupId}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export interface FileProps {
  file: IProductFile,
  relationProductId: string | undefined,
  versionId: string,
  versionGroupId: string,
  isLastFile: boolean,
  getData: () => void
}

const names: any[] = [
  { value: ProductFileEnum.MainProduct, label: "Hovedprodukt" },
  { value: ProductFileEnum.Patch, label: "Patch" },
  { value: ProductFileEnum.Other, label: "Annet" }
];

function File(props: FileProps) {
  const makeRequest = useMakeRequestHook();
  const [releaseDate, setReleaseDate] = useState<Date>(props.file.releaseDate);
  const [description, setDescription] = useState<string>(props.file.description);
  const [url, setUrl] = useState<string>(props.file.downloadUrl);
  const [type, setType] = useState<ProductFileEnum>(props.file.versionType);
  const [mainModuleVersion, setMainModuleVersion] = useState<string>(props.file.mainModuleVersion);
  const [isLastRelease, setIsLastRelease] = useState<boolean>(props.isLastFile);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const handleDescriptionChange = (event: any) => setDescription(event.target.value);
  const handleUrlChange = (event: any) => setUrl(event.target.value);
  const handleTypeChange = (event: any) => setType(event.target.value);
  const handleMainModuleVersionChange = (event: any) => setMainModuleVersion(event.target.value);
  const handleIsLastRelease = (event: React.ChangeEvent<Element>, checked: boolean) => setIsLastRelease(checked);

  const handleUpdateFile = () => {
    makeRequest(`relationproducts/${props.relationProductId}/versions/${props.versionId}/files`, 'PUT', {
      downloadUrl: url,
      description,
      versionType: type,
      mainModuleVersion,
      id: props.file.id,
      releaseDate,
      isLastRelease
    }).then(result => {
      setSnackbarMessage({ content: t("editVersion.fileUpdated"), type: 'success' });
      props.getData();
    }).catch(() => setSnackbarMessage({ content: t("editVersion.failedToUpdateFile"), type: "error" }));
  };

  const handleDeleteFile = () => {
    makeRequest(`productVersions/${props.versionGroupId}/versions/${props.versionId}/files/${props.file.id}`, `DELETE`)
      .then(result => {
        setSnackbarMessage({ content: t("editVersion.fileDeleted"), type: 'success' });
        props.getData();
      }).catch(() => setSnackbarMessage({ content: t("editVersion.couldNotDeleteFile"), type: "error" }));
  };

  return <>
    <div className='d-flex flex-row align-items-end flex-wrap'>
      <div className='mb-3 me-3'>
        <DatePicker
          inputFormat="dd.MM.yyyy"
          label={t("editVersion.releaseDate")}
          value={releaseDate}
          onChange={(newDate: any) => {
            setReleaseDate(newDate);
          }}
        />
      </div>
      <div className='mb-3'>
        <GrayOutlinedButton
          variant='outlined'
          size='small'
          onClick={() => setReleaseDate(new Date())}>
          {t("editVersion.selectTodaysDate")}
        </GrayOutlinedButton>
      </div>
    </div>
    <div className='mb-3'>
      <TextField
        fullWidth
        label={t("standard.description")}
        name='description'
        value={description || ''}
        onChange={handleDescriptionChange}
      />
    </div>
    <div className='mb-3'>
      <TextField
        fullWidth
        label='Url'
        name='url'
        value={url || ''}
        onChange={handleUrlChange}
      />
    </div>
    <Row>
      <Col md={6}>
        <div className='mb-3'>
          <Select
            fullWidth
            label={t("editVersion.fileType")}
            name='type'
            displayEmpty
            value={type || ''}
            onChange={handleTypeChange}>
            {names?.map(p => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </div>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <div className='mb-3'>
          <TextField
            fullWidth
            label={t("editVersion.programVersion")}
            name='mainModuleVersion'
            value={mainModuleVersion || ''}
            onChange={handleMainModuleVersionChange}
          />
        </div>
      </Col>
    </Row>
    <div className='mb-3'>
      <CheckboxLabel
        label={t("editVersion.useAsLastDownload")}
        checked={isLastRelease}
        onChange={handleIsLastRelease}
      />
    </div>
    <div className="d-flex flex-row align-items-center flex-wrap">
      <div className='me-3 me-sm-0'>
        <PrimaryButton
          variant="contained"
          onClick={handleUpdateFile}
          className='minButtonWidth'>
          {t("editVersion.updateFile")}
        </PrimaryButton>
      </div>
      <Divider orientation="vertical" variant="middle" flexItem className="mx-4 d-none d-sm-block" />
      <div>
        <SecondaryButton
          variant="outlined"
          onClick={handleDeleteFile}
          className='minButtonWidth'>
          {t("editVersion.deleteFile")}
        </SecondaryButton>
      </div>
    </div>
  </>;
}

export default EditVersionDialog;